import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Modal,
  Form,
  Col,
  Row,
  Table,
} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { useCurrency } from '../../../../context/currencyContext';
import Select from 'react-select';
import Swal from 'sweetalert2';
import './payment.css';

const themeColor = '#004aad';
const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

function Payment({ show, handleClose }) {
  const { formatAmount } = useCurrency();

  const [userLabId, setUserLabId] = useState('');
  const [backendError, setBackendError] = useState(false);

  const [paymentDate, setPaymentDate] = useState(new Date());
  const [amount, setAmount] = useState('');
  const [referenceNo, setReferenceNo] = useState('');

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [error, setError] = useState('');

  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountName, setAccountName] = useState('');

  const [memo, setMemo] = useState('');
  const [amountToApply, setAmountToApply] = useState(0);
  const [unappliedCredit, setUnappliedCredit] = useState(0);

  const [tableData, setTableData] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  const [paymentMethod, setPaymentMethod] = useState('');

  const [paymentMethods] = useState([
    { id: 'cash', name: 'Cash' },
    { id: 'cc', name: 'Credit Card' },
    { id: 'bank', name: 'Bank Transfer' },
    { id: 'cheque', name: 'Cheque' },
  ]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [lastStatementOnly, setLastStatementOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [lastRunDateStatements, setLastRunDateStatements] = useState([]);
  const [loadingStatements, setLoadingStatements] = useState(false);

  const skipAmountSyncRef = useRef(false);

  // ---------- Screen size (UI only: used for react-select sizing) ----------
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const onResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isMobile = screenWidth <= 767;
  const isXS = screenWidth <= 480;
  const isXXS = screenWidth <= 360;
  const selectHeight = isXXS ? 26 : isXS ? 28 : isMobile ? 30 : 34;
  const selectFont = isXXS
    ? '0.66rem'
    : isXS
    ? '0.7rem'
    : isMobile
    ? '0.75rem'
    : undefined;

  useEffect(() => {
    if (show) {
      const storedUserLabId = localStorage.getItem('userLabId');
      if (storedUserLabId) {
        const cleanedId = String(storedUserLabId).trim();
        setUserLabId(cleanedId);
        setBackendError(false);
      } else {
        setError('User identification not found. Please log in again.');
      }
    }
  }, [show]);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      border: '1px solid #ced4da',
      borderRadius: '.25rem',
      minHeight: `${selectHeight}px`,
      height: `${selectHeight}px`,
      fontSize: selectFont,
      boxShadow: 'none',
      '&:hover': {
        border: '1px solid #ced4da',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: `${selectHeight - 2}px`,
      padding: isMobile ? '0 6px' : '0 8px',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: `${selectHeight - 2}px`,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: isMobile ? '4px' : provided.padding,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 99999,
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: selectFont,
      borderRadius: '.25rem',
      border: '1px solid #ced4da',
      boxShadow: '0 0.5rem 1rem rgba(0,0,0,.15)',
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: selectFont,
      padding: isMobile ? '6px 10px' : provided.padding,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      backgroundColor: state.isSelected ? themeColor : 'white',
      color: state.isSelected ? 'white' : 'black',
      '&:hover': {
        backgroundColor: state.isSelected ? themeColor : '#f8f9fa',
        color: state.isSelected ? 'white' : 'black',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: selectFont,
      color: '#212529',
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: selectFont,
      color: '#6c757d',
    }),
    input: (provided) => ({
      ...provided,
      fontSize: selectFont,
    }),
  };

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoadingAccounts(true);
        setError('');
        if (!userLabId) {
          setLoadingAccounts(false);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/api/account/lab-accounts/${userLabId}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            setAccounts([]);
            setError('No accounts found for this lab.');
          } else {
            throw new Error(`Failed to fetch accounts: ${response.status}`);
          }
        } else {
          const result = await response.json();
          if (result.success && Array.isArray(result.data)) {
            const sortedAccounts = [...result.data].sort((a, b) => {
              const nameA = (a.account_name || '').toLowerCase();
              const nameB = (b.account_name || '').toLowerCase();
              return nameA.localeCompare(nameB);
            });
            setAccounts(sortedAccounts);
            setBackendError(false);
          } else {
            setAccounts([]);
          }
        }
      } catch (fetchError) {
        setBackendError(true);
        setError(
          'Failed to connect to server. Please ensure the backend server is running on port 5002.'
        );
        setAccounts([]);
      } finally {
        setLoadingAccounts(false);
      }
    };

    if (show && userLabId) {
      fetchAccounts();
    } else if (show && !userLabId) {
      setLoadingAccounts(false);
      setAccounts([]);
    }
  }, [show, userLabId]);

  useEffect(() => {
    const fetchLastStatements = async () => {
      if (!show) return;
      try {
        setLoadingStatements(true);
        const response = await fetch(
          `${API_BASE_URL}/api/statements/last-run-date`
        );
        if (!response.ok) throw new Error('Cannot connect to server');
        const result = await response.json();
        if (result.success && result.data?.statements) {
          setLastRunDateStatements(result.data.statements);
        } else {
          setLastRunDateStatements([]);
        }
      } catch (error) {
        setLastRunDateStatements([]);
      } finally {
        setLoadingStatements(false);
      }
    };

    if (show) {
      fetchLastStatements();
    }
  }, [show]);

  useEffect(() => {
    if (selectedAccount) {
      const account = accounts.find((acc) => acc._id === selectedAccount);
      setSelectedAccountId(account ? account.accountId : '');
      setAccountName(account ? account.account_name : '');
    } else {
      setSelectedAccountId('');
      setAccountName('');
      setSelectedInvoices([]);
      setReferenceNo('');
      setAmount('');
      setAmountToApply(0);
      setUnappliedCredit(0);
      setTableData([]);
    }
  }, [selectedAccount, accounts]);

  const parseInvoiceNumber = (invoiceNumber) => {
    if (!invoiceNumber) return { prefix: '', number: 0 };
    const match = invoiceNumber.toString().match(/(\D*)(\d+)/);
    if (match) {
      return {
        prefix: match[1] || '',
        number: parseInt(match[2], 10) || 0,
      };
    }
    return { prefix: invoiceNumber.toString(), number: 0 };
  };

  useEffect(() => {
    const fetchInvoices = async (accountId) => {
      if (!accountId) {
        setTableData([]);
        setSelectedInvoices([]);
        setReferenceNo('');
        setAmount('');
        setAmountToApply(0);
        setUnappliedCredit(0);
        return;
      }

      try {
        setError('');
        const response = await fetch(
          `${API_BASE_URL}/api/invoices/invoices/account?accountId=${accountId}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch invoices: ${response.status}`);
        }

        const invoices = await response.json();
        const formattedInvoices = invoices.map((invoice) => {
          const parsedInvoiceNumber = parseInvoiceNumber(invoice.invoiceNumber);
          return {
            id: invoice._id || invoice.invoiceNumber,
            invoiceNumber: invoice.invoiceNumber,
            description: `Invoice ${invoice.invoiceNumber} / Case ${invoice.caseNumber || ''}`,
            invoiceDate: format(new Date(invoice.invoiceDate), 'yyyy-MM-dd'),
            invoiceDateObj: new Date(invoice.invoiceDate),
            total: invoice.total || 0,
            paid: invoice.paid || 0,
            originalRemaining: Math.max(0, (invoice.total || 0) - (invoice.paid || 0)),
            remaining: Math.max(0, (invoice.total || 0) - (invoice.paid || 0)),
            payment: 0,
            parsedInvoiceNumber,
          };
        });

        const sortedInvoices = [...formattedInvoices].sort((a, b) => {
          const aParsed = a.parsedInvoiceNumber;
          const bParsed = b.parsedInvoiceNumber;
          if (aParsed.prefix < bParsed.prefix) return -1;
          if (aParsed.prefix > bParsed.prefix) return 1;
          return aParsed.number - bParsed.number;
        });

        let filteredInvoices = sortedInvoices;

        if (showFilters) {
          if (startDate || endDate) {
            filteredInvoices = filteredInvoices.filter((inv) => {
              const invDate = new Date(inv.invoiceDate + 'T00:00:00');
              const start = startDate ? new Date(startDate) : null;
              const end = endDate ? new Date(endDate) : null;
              if (start) start.setHours(0, 0, 0, 0);
              if (end) end.setHours(23, 59, 59, 999);
              if (start && invDate < start) return false;
              if (end && invDate > end) return false;
              return true;
            });
          }

          if (lastStatementOnly && lastRunDateStatements.length > 0) {
            const accountStatements = lastRunDateStatements.filter(
              (statement) => statement.accountId === accountId
            );

            if (accountStatements.length > 0) {
              const lastStatementInvoiceNumbers = new Set();
              accountStatements.forEach((statement) => {
                if (statement.invoices && Array.isArray(statement.invoices)) {
                  statement.invoices.forEach((invoice) => {
                    if (invoice.invoiceNumber) {
                      lastStatementInvoiceNumbers.add(invoice.invoiceNumber);
                    }
                  });
                }
              });

              filteredInvoices = filteredInvoices.filter((invoice) =>
                lastStatementInvoiceNumbers.has(invoice.invoiceNumber)
              );
            } else {
              filteredInvoices = [];
            }
          }
        }

        filteredInvoices = filteredInvoices.filter((inv) => inv.remaining > 0);

        const finalInvoices = filteredInvoices.map(
          ({ parsedInvoiceNumber, ...rest }) => ({
            ...rest,
            payment: rest.originalRemaining,
            remaining: 0,
          })
        );

        setTableData(finalInvoices);

        const updatedSelectedInvoices = finalInvoices.map((row) => row.id);
        setSelectedInvoices(updatedSelectedInvoices);
      } catch (invoiceError) {
        setError('Error fetching invoices: ' + invoiceError.message);
        setTableData([]);
        setSelectedInvoices([]);
        setReferenceNo('');
        setAmount('');
        setAmountToApply(0);
        setUnappliedCredit(0);
      }
    };

    fetchInvoices(selectedAccountId);
  }, [
    selectedAccountId,
    startDate,
    endDate,
    showFilters,
    lastStatementOnly,
    lastRunDateStatements,
  ]);

  useEffect(() => {
    const selectedRows = tableData.filter((row) =>
      selectedInvoices.includes(row.id)
    );
    const invoiceNumbers = selectedRows.map((row) => row.invoiceNumber);
    setReferenceNo(invoiceNumbers.join(', '));
  }, [selectedInvoices, tableData]);

  useEffect(() => {
    const totalApplied = tableData.reduce(
      (sum, row) => sum + parseFloat(row.payment || 0),
      0
    );

    setAmountToApply(totalApplied);

    const numericAmount = parseFloat(amount || 0);

    if (!isNaN(numericAmount)) {
      const unapplied = numericAmount - totalApplied;
      setUnappliedCredit(unapplied > 0 ? unapplied : 0);
    } else {
      setUnappliedCredit(0);
    }
  }, [amount, tableData]);

  useEffect(() => {
    if (skipAmountSyncRef.current) {
      skipAmountSyncRef.current = false;
      return;
    }

    const calculateTotalFromSelectedInvoices = () => {
      return selectedInvoices.reduce((sum, invoiceId) => {
        const invoice = tableData.find((row) => row.id === invoiceId);
        return sum + (invoice ? parseFloat(invoice.payment || 0) : 0);
      }, 0);
    };

    if (selectedInvoices.length > 0) {
      const totalPayment = calculateTotalFromSelectedInvoices();
      setAmount(totalPayment > 0 ? totalPayment.toString() : '');
    } else if (selectedInvoices.length === 0 && !selectedAccountId) {
      setAmount('');
    }
  }, [selectedInvoices, tableData]);

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === 'account') {
      setSelectedAccount(value);
    } else if (name === 'amount') {
      setAmount(value);
      skipAmountSyncRef.current = true;

      if (value && !isNaN(parseFloat(value)) && selectedAccountId) {
        let remainingAmount = parseFloat(value);
        const newSelectedInvoices = [];

        const updatedTableData = tableData.map((row) => ({
          ...row,
          payment: 0,
          remaining: row.originalRemaining,
        }));

        const sortedInvoices = [...updatedTableData].sort((a, b) => {
          const dateA = new Date(a.invoiceDate);
          const dateB = new Date(b.invoiceDate);
          if (dateA < dateB) return -1;
          if (dateA > dateB) return 1;
          return a.invoiceNumber.localeCompare(b.invoiceNumber);
        });

        const finalTableData = sortedInvoices.map((row) => {
          if (remainingAmount <= 0) return row;

          if (row.originalRemaining > 0) {
            if (remainingAmount >= row.originalRemaining) {
              newSelectedInvoices.push(row.id);
              remainingAmount -= row.originalRemaining;
              return {
                ...row,
                payment: row.originalRemaining,
                remaining: 0,
              };
            } else {
              newSelectedInvoices.push(row.id);
              const paymentAmount = remainingAmount;
              remainingAmount = 0;
              return {
                ...row,
                payment: paymentAmount,
                remaining: row.originalRemaining - paymentAmount,
              };
            }
          }
          return row;
        });

        setSelectedInvoices(newSelectedInvoices);
        setTableData(finalTableData);
      } else if (!value) {
        setSelectedInvoices([]);
        setTableData(
          tableData.map((row) => ({
            ...row,
            payment: 0,
            remaining: row.originalRemaining,
          }))
        );
      }
    } else if (name === 'referenceNo') {
      setReferenceNo(value);
    } else if (name === 'memo') {
      setMemo(value);
    } else if (name === 'paymentMethod') {
      setPaymentMethod(value);
    } else if (name === 'lastStatementOnly') {
      setLastStatementOnly(checked);
    }
  };

  const handleInvoiceSelection = (invoiceId) => {
    const isSelected = selectedInvoices.includes(invoiceId);

    if (isSelected) {
      setSelectedInvoices((prev) => prev.filter((id) => id !== invoiceId));
      setTableData((prev) =>
        prev.map((row) =>
          row.id === invoiceId
            ? { ...row, payment: 0, remaining: row.originalRemaining }
            : row
        )
      );
    } else {
      setSelectedInvoices((prev) => [...prev, invoiceId]);
      setTableData((prev) =>
        prev.map((row) =>
          row.id === invoiceId
            ? { ...row, payment: row.originalRemaining, remaining: 0 }
            : row
        )
      );
    }
  };

  const handleInvoicePaymentChange = (index, paymentValue) => {
    const updatedTableData = [...tableData];
    const row = updatedTableData[index];
    const newPayment = parseFloat(paymentValue || 0);

    if (row) {
      const maxAllowedPayment = row.originalRemaining;
      const appliedPayment = Math.min(newPayment, maxAllowedPayment);

      updatedTableData[index] = {
        ...row,
        payment: appliedPayment,
        remaining: row.originalRemaining - appliedPayment,
      };

      setTableData(updatedTableData);

      if (appliedPayment > 0 && !selectedInvoices.includes(row.id)) {
        setSelectedInvoices((prev) => [...prev, row.id]);
      }
    }
  };

  const handleSave = async () => {
    if (!userLabId) {
      Swal.fire({
        title: 'Error!',
        text: 'Unable to process payment: User identification not found. Please log in again.',
        icon: 'error',
        confirmButtonColor: themeColor,
      });
      return;
    }

    if (!selectedAccount || !paymentDate || !paymentMethod || !amount) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields (*).',
        icon: 'warning',
        confirmButtonColor: themeColor,
      });
      return;
    }

    const numericAmount = parseFloat(amount || 0);

    if (isNaN(numericAmount) || numericAmount <= 0) {
      Swal.fire({
        title: 'Invalid Amount',
        text: 'Please enter a valid positive amount.',
        icon: 'warning',
        confirmButtonColor: themeColor,
      });
      return;
    }

    const appliedInvoices = tableData
      .filter(
        (row) =>
          selectedInvoices.includes(row.id) && parseFloat(row.payment || 0) > 0
      )
      .map((row) => {
        const existingPaid = parseFloat(row.paid || 0);
        const newPayment = parseFloat(row.payment || 0);
        return {
          invoiceNumber: row.invoiceNumber,
          appliedAmount: newPayment,
          newPaidAmount: existingPaid + newPayment,
        };
      });

    const totalAppliedFromInvoices = appliedInvoices.reduce(
      (sum, inv) => sum + inv.appliedAmount,
      0
    );

    if (totalAppliedFromInvoices > numericAmount) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Applied amount cannot exceed the payment amount.',
        icon: 'warning',
        confirmButtonColor: themeColor,
      });
      return;
    }

    let referenceNoArray = selectedInvoices
      .map((id) => tableData.find((row) => row.id === id)?.invoiceNumber)
      .filter(Boolean);

    if (referenceNoArray.length === 0) {
      referenceNoArray = [
        `PAY-${format(paymentDate, 'yyyyMMdd')}-${Date.now()
          .toString()
          .slice(-6)}`,
      ];
    }

    const paymentData = {
      accountId: selectedAccountId,
      accountName,
      amountReceived: numericAmount,
      paymentDate: format(paymentDate, 'yyyy-MM-dd'),
      paymentMethod,
      referenceNo: referenceNoArray,
      memo,
      appliedInvoices,
      finalAppliedAmount: amountToApply,
      finalUnappliedAmount: unappliedCredit,
      addedBy: userLabId,
      uniquePaymentId: `PAY-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
    };

    try {
      const paymentResponse = await fetch(`${API_BASE_URL}/api/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(paymentData),
      });

      if (!paymentResponse.ok) {
        const errorData = await paymentResponse.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to save payment: ${paymentResponse.status}`
        );
      }

      const responseData = await paymentResponse.json();

      if (responseData.success) {
        Swal.fire({
          title: 'Success!',
          text: responseData.message || 'Payment saved successfully!',
          icon: 'success',
          confirmButtonColor: themeColor,
        });
        handleCloseModal();
      } else {
        throw new Error(responseData.message || 'Failed to save payment');
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Failed to save payment: ' + error.message,
        icon: 'error',
        confirmButtonColor: themeColor,
      });
    }
  };

  const handleCloseModal = () => {
    setSelectedAccount('');
    setSelectedAccountId('');
    setAccountName('');
    setPaymentDate(new Date());
    setAmount('');
    setReferenceNo('');
    setMemo('');
    setTableData([]);
    setSelectedInvoices([]);
    setAmountToApply(0);
    setUnappliedCredit(0);
    setError('');
    setLoadingAccounts(true);
    setPaymentMethod('');
    setStartDate(null);
    setEndDate(null);
    setLastStatementOnly(false);
    setShowFilters(false);
    setLastRunDateStatements([]);
    setLoadingStatements(false);
    setBackendError(false);
    handleClose();
  };

  const CustomDateInput = React.forwardRef(({ value, onClick, name }, ref) => (
    <div className="payment-date-picker-wrapper">
      <Form.Control
        type="text"
        value={value}
        onClick={onClick}
        ref={ref}
        readOnly
        name={name}
        className="payment-date-input"
      />
      <button type="button" className="payment-calendar-btn" onClick={onClick}>
        <svg width="15" height="15" fill="currentColor" viewBox="0 0 16 16">
          <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H1a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
          <path d="M2.5 7a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5V7z"/>
        </svg>
      </button>
    </div>
  ));

  return (
    <Modal
      show={show}
      onHide={handleCloseModal}
      backdrop="static"
      keyboard={false}
      size="lg"
      dialogClassName="custom-payment-modal-dialog"
      contentClassName="custom-payment-modal-content"
      className="create-payment-modal"
    >
      <Modal.Header closeButton className="payment-modal-header">
        <Modal.Title className="payment-modal-title">
          Create New Payment
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="payment-modal-body">
        {error && <div className="alert alert-danger py-2">{error}</div>}
        {backendError && (
          <div className="alert alert-warning py-2">
            <strong>Connection Issue:</strong> Unable to connect to the server.
          </div>
        )}

        <Form onSubmit={(e) => e.preventDefault()}>
          <h5 className="payment-section-title">Payment Details</h5>

          <div className="payment-grey-box">
            <Row className="mb-2 align-items-center payment-row-field">
              <Col xs={12} md={6} className="d-flex align-items-center payment-col-field">
                <label className="payment-label">
                  Account <span className="payment-required">*</span>
                </label>
                <div className="payment-field-control">
                  <Select
                    classNamePrefix="payment-select"
                    styles={customStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    options={accounts.map((acc) => ({
                      value: acc._id,
                      label: acc.account_name,
                    }))}
                    value={
                      selectedAccount
                        ? { value: selectedAccount, label: accountName }
                        : null
                    }
                    onChange={(selectedOption) =>
                      handleInputChange({
                        target: {
                          name: 'account',
                          value: selectedOption ? selectedOption.value : '',
                        },
                      })
                    }
                    placeholder="ALL SAINTS DENTAL"
                  />
                </div>
              </Col>
              <Col xs={12} md={6} className="d-flex align-items-center payment-col-field">
                <label className="payment-label payment-label-md-ms">
                  Amount <span className="payment-required">*</span>
                </label>
                <Form.Control
                  type="text"
                  name="amount"
                  value={amount}
                  onChange={handleInputChange}
                  className="payment-input-control"
                />
              </Col>
            </Row>

            <Row className="mb-2 align-items-center payment-row-field">
              <Col xs={12} md={6} className="d-flex align-items-center payment-col-field">
                <label className="payment-label">
                  Payment Date <span className="payment-required">*</span>
                </label>
                <div className="payment-field-control">
                  <DatePicker
                    selected={paymentDate}
                    onChange={(date) => setPaymentDate(date)}
                    dateFormat="d MMM yyyy"
                    customInput={<CustomDateInput name="paymentDate" />}
                  />
                </div>
              </Col>
              <Col xs={12} md={6} className="d-flex align-items-center payment-col-field">
                <label className="payment-label payment-label-md-ms">Reference No.</label>
                <Form.Control
                  type="text"
                  name="referenceNo"
                  value={referenceNo}
                  onChange={handleInputChange}
                  className="payment-input-control"
                />
              </Col>
            </Row>

            <Row className="align-items-center payment-row-field">
              <Col xs={12} md={6} className="d-flex align-items-center payment-col-field">
                <label className="payment-label">
                  Payment Method <span className="payment-required">*</span>
                </label>
                <div className="payment-field-control">
                  <Form.Select
                    name="paymentMethod"
                    value={paymentMethod}
                    onChange={handleInputChange}
                    className="payment-input-control"
                  >
                    <option value="">Select Payment Method</option>
                    {paymentMethods.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </Col>
            </Row>
          </div>

          <div className="d-flex align-items-center mb-2 mt-3 payment-header-flex">
            <h5 className="payment-section-title border-0 mb-0 p-0 me-2">
              Apply to Outstanding Invoices
            </h5>
            <span
              className="payment-filter-toggle-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              (<span className="filter-box-icon">▶</span> filter)
            </span>
          </div>

          {showFilters && (
            <div className="payment-filter-box">
              <Row className="align-items-center g-2">
                <Col xs={12} md={5} className="d-flex align-items-center payment-filter-col">
                  <label className="payment-filter-label">From Date:</label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    dateFormat="yyyy-MM-dd"
                    className="form-control form-control-sm"
                    placeholderText="Select Start Date"
                  />
                </Col>
                <Col xs={12} md={5} className="d-flex align-items-center payment-filter-col">
                  <label className="payment-filter-label">To Date:</label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    dateFormat="yyyy-MM-dd"
                    className="form-control form-control-sm"
                    placeholderText="Select End Date"
                  />
                </Col>
                <Col xs={12} md={2}>
                  <Form.Check
                    type="checkbox"
                    label="Last Statement Only"
                    name="lastStatementOnly"
                    checked={lastStatementOnly}
                    onChange={handleInputChange}
                    className="payment-last-statement-check"
                  />
                </Col>
              </Row>
            </div>
          )}

          <div className="payment-table-container">
            <Table className="payment-custom-table align-middle">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Description</th>
                  <th style={{ width: '130px' }}>Invoice Date</th>
                  <th style={{ width: '110px' }}>Total</th>
                  <th style={{ width: '110px' }}>Remaining</th>
                  <th style={{ width: '130px' }}>Payment</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-3">
                      No outstanding invoices found.
                    </td>
                  </tr>
                ) : (
                  tableData.map((row, index) => {
                    const isChecked = selectedInvoices.includes(row.id);
                    return (
                      <tr key={row.id}>
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleInvoiceSelection(row.id)}
                            className="payment-row-checkbox"
                          />
                        </td>
                        <td>{row.description}</td>
                        <td>{row.invoiceDate}</td>
                        <td>£{parseFloat(row.total).toFixed(2)}</td>
                        <td>£{parseFloat(row.remaining).toFixed(2)}</td>
                        <td>
                          <Form.Control
                            type="number"
                            value={row.payment || ''}
                            onChange={(e) =>
                              handleInvoicePaymentChange(index, e.target.value)
                            }
                            className="payment-table-input"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <Row className="mt-3 align-items-start payment-bottom-row">
            <Col xs={12} md={6} className="payment-bottom-col">
              <Form.Group controlId="paymentMemo">
                <Form.Label className="payment-memo-label mb-1">
                  Memo / Note
                </Form.Label>

                <Form.Control
                  as="textarea"
                  rows={2}
                  name="memo"
                  value={memo}
                  onChange={handleInputChange}
                  placeholder="Optional payment details or reference notes..."
                  className="payment-memo-textarea"
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6} className="payment-summary-col payment-bottom-col">
              <div className="payment-summary-row mb-1">
                <span className="payment-summary-label">
                  Amount to Apply:
                </span>
                <span className="payment-summary-value">
                  £{amountToApply.toFixed(2)}
                </span>
              </div>

              <div className="payment-unapplied-row">
                <span className="payment-unapplied-label">
                  Credit amount exceeding the outstanding invoices total creates a new unapplied credit:
                </span>
                <span className="payment-unapplied-value">
                  £{unappliedCredit.toFixed(2)}
                </span>
              </div>
            </Col>
          </Row>

          <div className="d-flex justify-content-end mt-3 payment-footer-btns">
            <Button
              variant="secondary"
              className="payment-btn-cancel me-2"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="payment-btn-save"
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default Payment;